import { ROUTES } from '@/constants';
import { EventType } from '@sportspredict/shared';
import type { ProbabilityEvent } from '../types/probability-event.types';

export const PROBABILITY_EVENT_PAGE_NAME = 'ProbabilityEvent';

export const SOCKET_REFETCH_DELAY = 8000;

export const PROBABILITY_EVENT_TABS = {
	HOME: 'home',
	LEADERBOARD: 'leaderboard',
	FRIENDS: 'friends',
	PROFILE: 'profile',
	BOTS: 'bots',
	MARKETS: 'markets',
	ACHIEVEMENTS: 'achievements',
} as const;

export type ProbabilityEventTab =
	(typeof PROBABILITY_EVENT_TABS)[keyof typeof PROBABILITY_EVENT_TABS];

export const isProbabilityEvent = (event?: ProbabilityEvent | null) =>
	event?.type === EventType.Probability;

export const sortProbabilityEventsFirst = (events: ProbabilityEvent[] = []) =>
	[...events].sort((eventA, eventB) => {
		const aIsProbability = isProbabilityEvent(eventA);
		const bIsProbability = isProbabilityEvent(eventB);

		if (aIsProbability === bIsProbability) return 0;
		return aIsProbability ? -1 : 1;
	});

export const getProbabilityEventIdFromPath = (pathname: string) => {
	const normalizedPath = pathname.split('?')[0].replace(/\/+$/, '');
	const prefix = `${ROUTES.PROBABILITY_EVENT}/`;

	if (!normalizedPath.startsWith(prefix)) return null;

	const [eventId] = normalizedPath.slice(prefix.length).split('/');
	return eventId || null;
};

export const getProbabilityEventTabPath = (
	eventId: string,
	tab: ProbabilityEventTab = PROBABILITY_EVENT_TABS.HOME
) => `${ROUTES.PROBABILITY_EVENT}/${eventId}/${tab}`;

export const getProbabilityEventPath = (eventId: string) => getProbabilityEventTabPath(eventId);

export const getProbabilityEventMarketsPath = (eventId: string, matchId?: string) => {
	const path = getProbabilityEventTabPath(eventId, PROBABILITY_EVENT_TABS.MARKETS);
	return matchId ? `${path}?matchId=${encodeURIComponent(matchId)}` : path;
};

export const getProbabilityEventNewsfeedPath = (eventId: string) =>
	`${ROUTES.PROBABILITY_EVENT}/${eventId}/newsfeed`;

export const getProbabilityEventFriendsPath = (eventId: string) =>
	getProbabilityEventTabPath(eventId, PROBABILITY_EVENT_TABS.FRIENDS);

export const getProbabilityEventFriendsSearchPath = (eventId: string) =>
	`${getProbabilityEventFriendsPath(eventId)}/search`;

export const getProbabilityEventSharedProfileUrl = (eventId: string, userId: string) =>
	`${getProbabilityEventTabPath(eventId, PROBABILITY_EVENT_TABS.LEADERBOARD)}?profile=${encodeURIComponent(userId)}`;

export const getProbabilityEventHistoryPath = (eventId: string, matchId: string) =>
	`${ROUTES.PROBABILITY_EVENT}/${eventId}/history/${matchId}`;

export type AchievementsNavigateFrom = 'profile' | 'bots';

export const resolveAchievementsPathOptions = (options?: {
	from?: AchievementsNavigateFrom;
	botId?: string;
	userId?: string;
}) => {
	const from = options?.from;
	const botId = options?.botId ?? (from === 'bots' ? options?.userId : undefined);
	return { from, botId };
};

export const getProbabilityEventAchievementsPath = (
	eventId: string,
	options?: { botId?: string; from?: AchievementsNavigateFrom; userId?: string }
) => {
	const { from, botId } = resolveAchievementsPathOptions(options);
	const base = `${ROUTES.PROBABILITY_EVENT}/${eventId}/${PROBABILITY_EVENT_TABS.ACHIEVEMENTS}`;
	const params = new URLSearchParams();
	if (botId) params.set('botId', botId);
	if (from) params.set('from', from);
	const query = params.toString();
	return query ? `${base}?${query}` : base;
};

export const getProbabilityEventBotDetailsPath = (eventId: string, botId: string) =>
	`${ROUTES.PROBABILITY_EVENT}/${eventId}/bot/${botId}`;

export const getProbabilityEventBotsPath = (eventId: string, botId?: string) => {
	const base = getProbabilityEventTabPath(eventId, PROBABILITY_EVENT_TABS.BOTS);
	return botId ? `${base}?botId=${encodeURIComponent(botId)}` : base;
};

export const getAchievementsBackPath = (
	eventId: string,
	from: string | null | undefined,
	botId?: string | null
) => {
	if (from === 'profile')
		return getProbabilityEventTabPath(eventId, PROBABILITY_EVENT_TABS.PROFILE);
	if (from === 'bots') return getProbabilityEventBotsPath(eventId, botId ?? undefined);
	return getProbabilityEventPath(eventId);
};

export type PerformanceSummaryTabSlug = 'highlights' | 'category' | 'matches' | 'calibration';

export const getProbabilityEventPerformanceSummaryPath = (
	eventId: string,
	options?: { tab?: PerformanceSummaryTabSlug; botId?: string }
) => {
	const base = `${ROUTES.PROBABILITY_EVENT}/${eventId}/performance-summary`;
	const params = new URLSearchParams();
	if (options?.tab) params.set('tab', options.tab);
	if (options?.botId) params.set('botId', options.botId);
	const qs = params.toString();
	return qs ? `${base}?${qs}` : base;
};

export const isProbabilityEventEditProfilePath = (pathname: string) =>
	pathname.split('?')[0].replace(/\/+$/, '').endsWith('/edit-profile');
